import { InfiniteTable } from '@/components/_common';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import { useYardReportList } from '@/core/domain/visual-management/yard-report/yard-report.hooks';
import {
  YardReportFormValues,
  YardReportListItem,
} from '@/core/domain/visual-management/yard-report/yard-report.types';
import { useDebounce } from '@/hooks';
import { DateTimeFormat } from '@/utils/moment';
import { getTableScrollX } from '@/utils/table';
import { Form, FormInstance, List, Popover } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import {
  AppstoreOutlined,
  TeamOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import EntityAvatar from '@/components/_common/EntityAvatar';

interface Props {
  form: FormInstance<YardReportFormValues>;
}

export default function YardManagementReportList(props: Props) {
  const values = Form.useWatch([], props.form);

  const queryDebounce = useDebounce(values);

  const yardReportList = useYardReportList({
    statusIds: queryDebounce?.statusIds,
    dateFilter: queryDebounce?.dateFilter,
    situationIds: queryDebounce?.situationIds,
    vehicleIds: queryDebounce?.vehicleIds?.toString(),
    companyIds: queryDebounce?.companiesIds?.toString(),
    customerIds: queryDebounce?.customerIds?.toString(),
    estimatorIds: queryDebounce?.estimatorIds?.toString(),
    technicianIds: queryDebounce?.technicianIds?.toString(),
    endDate: queryDebounce?.finalDate.format(DateTimeFormat),
    startDate: queryDebounce?.initialDate.format(DateTimeFormat),
  });

  const list = yardReportList.data?.pages.flatMap((pages) => pages.items);

  const columns: ColumnsType<YardReportListItem> = [
    {
      dataIndex: 'plate',
      title: 'Placa',
      ellipsis: true,
      width: 80,
      render: (value) => value || '-',
    },
    {
      dataIndex: 'vehicle',
      title: 'Veículo',
      ellipsis: true,
      width: 200,
      render: (value) => (
        <EllipsisHoverToggle>{value || '-'}</EllipsisHoverToggle>
      ),
    },
    {
      dataIndex: 'complementIdentifier',
      title: 'Orçamento',
      ellipsis: true,
      width: 120,
      render: (value) => value || '-',
    },
    {
      dataIndex: 'entryDate',
      title: 'Data',
      ellipsis: true,
      width: 140,
      render: (value) => moment(value).format('DD/MM/YYYY HH:mm'),
    },
    {
      dataIndex: 'technicians',
      title: 'Funcionários',
      ellipsis: true,
      width: 90,
      align: 'center',
      render: (value: string[]) => {
        const hasTechs = value && !!value.length;

        return (
          <Popover
            title="Funcionários"
            content={
              <div style={{ width: 300, maxHeight: 300, overflowY: 'auto' }}>
                {hasTechs ? (
                  <List
                    dataSource={value}
                    renderItem={(name) => (
                      <List.Item key={name}>
                        <List.Item.Meta
                          avatar={<EntityAvatar name={name} />}
                          title={name}
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  'Nenhum funcionário'
                )}
              </div>
            }
          >
            <TeamOutlined
              style={{
                fontSize: 20,
                color: hasTechs ? '#38b3ca' : 'dimgray',
              }}
            />
          </Popover>
        );
      },
    },
    {
      dataIndex: 'parts',
      title: 'Peças',
      width: 90,
      align: 'center',
      render: (value: string[]) => {
        const hasItems = value && !!value.length;

        return (
          <Popover
            title="Peças"
            content={
              <div style={{ width: 300, maxHeight: 300, overflowY: 'auto' }}>
                {hasItems ? (
                  <ul style={{ paddingLeft: 20 }}>
                    {value.map((part, i) => (
                      <li key={i}>{part}</li>
                    ))}
                  </ul>
                ) : (
                  'Nenhuma peça'
                )}
              </div>
            }
          >
            <ToolOutlined
              style={{
                fontSize: 20,
                color: hasItems ? '#38b3ca' : 'dimgray',
              }}
            />
          </Popover>
        );
      },
    },
    {
      dataIndex: 'services',
      title: 'Serviços',
      width: 90,
      align: 'center',
      render: (value: string[]) => {
        const hasItems = value && value.length > 0;

        return (
          <Popover
            title="Serviços"
            content={
              <div style={{ width: 300, maxHeight: 300, overflowY: 'auto' }}>
                {hasItems ? (
                  <ul style={{ paddingLeft: 20 }}>
                    {value.map((service, i) => (
                      <li key={i}>{service}</li>
                    ))}
                  </ul>
                ) : (
                  'Nenhum serviço'
                )}
              </div>
            }
          >
            <AppstoreOutlined
              style={{
                fontSize: 20,
                color: hasItems ? '#38b3ca' : 'dimgray',
              }}
            />
          </Popover>
        );
      },
    },
    {
      dataIndex: 'customer',
      width: 150,
      ellipsis: true,
      title: 'Cliente',
    },
    {
      dataIndex: 'estimator',
      title: 'Orçamentista',
      ellipsis: true,
      width: 150,
      render: (value) => value || '-',
    },
    {
      dataIndex: 'situation',
      title: 'Situação',
      ellipsis: true,
      width: 150,
      render: (value) => value || '-',
    },
    {
      dataIndex: 'status',
      title: 'Status',
      ellipsis: true,
      width: 150,
      render: (value) => value || '-',
    },
  ];

  return (
    <InfiniteTable
      columns={columns}
      dataSource={list}
      loading={yardReportList.isFetching}
      scroll={{ x: getTableScrollX(columns) }}
      fetchNextPage={yardReportList.fetchNextPage}
      hasNextPage={yardReportList.hasNextPage}
    />
  );
}
